import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import MasterImportDialog, { cellText } from '../../common/MasterImportDialog';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const defaultForm = {
  departmentCode: '',
  departmentName: '',
  status: 'ACTIVE',
};

const DEPARTMENT_IMPORT_MANDATORY = [
  { display: 'Department Code', key: 'departmentCode', aliases: ['Code'] },
  { display: 'Department Name', key: 'departmentName', aliases: ['Name'] },
];

const DEPARTMENT_IMPORT_OPTIONAL = [
  { display: 'Status', key: 'status', aliases: [] },
];

const mapDepartmentRow = (item) => ({
  id: item.departmentId,
  departmentCode: item.departmentCode,
  departmentName: item.departmentName,
  status: item.status,
  raw: item,
});

const DepartmentMaster = () => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.DEPARTMENT_MASTER);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [focusedField, setFocusedField] = useState('');

  const focusLabelProps = (fieldName, hasValue) => {
    const active = focusedField === fieldName;
    return {
      shrink: hasValue || active,
      sx: (hasValue || active) ? { bgcolor: 'background.paper', px: 0.5 } : undefined,
    };
  };

  const fieldFocus = (fieldName) => ({
    onFocus: () => setFocusedField(fieldName),
    onBlur: () => setFocusedField((current) => (current === fieldName ? '' : current)),
  });

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchDepartments = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/department-masters/tenant/${tenantId}`);
      setRows((Array.isArray(data) ? data : []).map(mapDepartmentRow));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const columns = useMemo(() => [
    { field: 'departmentCode', headerName: 'Department Code', width: 160, resizable: true },
    { field: 'departmentName', headerName: 'Department Name', width: 260, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], []);

  const buildPayload = () => ({
    tenantId,
    departmentCode: form.departmentCode?.trim(),
    departmentName: form.departmentName?.trim(),
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setFocusedField('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setFocusedField('');
    setForm({
      departmentCode: selectedRow.departmentCode || '',
      departmentName: selectedRow.departmentName || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    if (!form.departmentCode?.trim() || !form.departmentName?.trim()) {
      showError('Department code and name are required');
      return;
    }
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/department-masters', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('Department saved successfully');
      } else {
        await apiFetch(`conductor/department-masters/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('Department updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchDepartments();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/department-masters/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchDepartments();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const buildDepartmentImportItems = ({ dataRows, columnIndexByKey }) => {
    const items = [];
    const errors = [];
    const userId = getUserId();
    dataRows.forEach((row, index) => {
      const departmentCode = cellText(row, columnIndexByKey, 'departmentCode');
      const departmentName = cellText(row, columnIndexByKey, 'departmentName');
      if (!departmentCode && !departmentName) return;
      if (!departmentCode || !departmentName) {
        errors.push(
          `Row ${index + 2}: Department Code and Department Name are both required`
        );
        return;
      }
      let status = (cellText(row, columnIndexByKey, 'status') || 'ACTIVE').toUpperCase();
      if (status && status !== 'ACTIVE' && status !== 'INACTIVE') {
        errors.push(`Row ${index + 2} (${departmentCode}): Status must be ACTIVE or INACTIVE`);
        return;
      }
      items.push({
        tenantId,
        departmentCode,
        departmentName,
        status: status || 'ACTIVE',
        createdBy: userId,
        updatedBy: userId,
        _rowLabel: departmentCode,
      });
    });
    return { items, errors };
  };

  const importDepartments = async (items) => {
    if (!canUpload) return;
    if (!tenantId) {
      throw new Error('Tenant is required for import');
    }
    const failed = [];
    let successCount = 0;
    for (const item of items) {
      const { _rowLabel, ...payload } = item;
      try {
        await apiFetch('conductor/department-masters', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        successCount += 1;
      } catch (err) {
        failed.push({
          rowLabel: _rowLabel || payload.departmentCode,
          error: err.message || 'Save failed',
        });
      }
    }
    return { successCount, failureCount: failed.length, failed };
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Department Master</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canUpload ? 'Import Departments' : 'No upload permission'}>
            <span>
              <IconButton onClick={() => { if (!canUpload) return; setImportOpen(true); }} disabled={!tenantId || !canUpload} sx={harmonatePlainIconButtonSx}>
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit Department' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add Department' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete Department' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Department' : 'Edit Department'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              required
              label="Department Code"
              value={form.departmentCode}
              onChange={(e) => setForm((p) => ({ ...p, departmentCode: e.target.value }))}
              InputLabelProps={focusLabelProps('departmentCode', Boolean(form.departmentCode))}
              {...fieldFocus('departmentCode')}
            />
            <TextField
              fullWidth
              size="small"
              required
              label="Department Name"
              value={form.departmentName}
              onChange={(e) => setForm((p) => ({ ...p, departmentName: e.target.value }))}
              InputLabelProps={focusLabelProps('departmentName', Boolean(form.departmentName))}
              {...fieldFocus('departmentName')}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              InputLabelProps={focusLabelProps('status', Boolean(form.status))}
              {...fieldFocus('status')}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete &quot;{rowPendingDelete?.departmentName || rowPendingDelete?.departmentCode || 'selected'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <MasterImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import departments"
        mandatoryHeaders={DEPARTMENT_IMPORT_MANDATORY}
        optionalHeaders={DEPARTMENT_IMPORT_OPTIONAL}
        dropHint="Each data row must include Department Code and Department Name."
        buildItems={buildDepartmentImportItems}
        onImport={importDepartments}
        onImportSuccess={fetchDepartments}
        showSuccess={showSuccess}
        showError={showError}
      />

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default DepartmentMaster;
